import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';

import { getPriceBookName } from 'dibs-cookie-jar';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';

import {
    type useNotificationsDeleteTagsMutation,
    type UserNotificationsTagEnum,
} from './__generated__/useNotificationsDeleteTagsMutation.graphql.ts';
import { type useNotificationsDeleteTagsAllMutation } from './__generated__/useNotificationsDeleteTagsAllMutation.graphql.ts';

type MarkNotificationsAsReadInput = {
    activityFeedTagIds?: string[];
    itemEventTagIds?: string[];
};

type DeleteTagFromAllNotificationsInput = {
    activityFeedTagIds?: string[];
    itemEventTagIds?: string[];
    conversationIds?: string[];
    userId: string;
    hasTradeUserId: boolean;
    tag: UserNotificationsTagEnum;
};

type UseNotificationsDeleteTagsResponse = {
    markNotificationsAsRead: ({
        activityFeedTagIds,
        itemEventTagIds,
    }: MarkNotificationsAsReadInput) => void;
    deleteTagFromAllNotifications: ({
        activityFeedTagIds,
        itemEventTagIds,
        conversationIds,
        userId,
        hasTradeUserId,
        tag,
    }: DeleteTagFromAllNotificationsInput) => void;
    deleteTagFromAllNotificationsInFlight: boolean;
};

export const useNotificationsDeleteTags = (): UseNotificationsDeleteTagsResponse => {
    const [deleteTagFromAllNotificationsMutation, deleteTagFromAllNotificationsInFlight] =
        useMutation<useNotificationsDeleteTagsAllMutation>(graphql`
            mutation useNotificationsDeleteTagsAllMutation(
                $input: UserNotificationsDeleteTagsInput!
                $hasTradeUserId: Boolean!
                $priceBookName: String!
                $userCountryCode: String!
                $userId: String!
            ) {
                userNotificationsDeleteTags(input: $input) {
                    viewer {
                        ...UserNotificationsMarkAllAsRead_viewer
                        ...UserNotificationsIcon_viewer
                        ...UserNotificationsTabs_viewer
                    }
                }
            }
        `);

    const [markUserNotificationsAsReadMutation] = useMutation<useNotificationsDeleteTagsMutation>(
        graphql`
            mutation useNotificationsDeleteTagsMutation($input: UserNotificationsDeleteTagsInput!) {
                userNotificationsDeleteTags(input: $input) {
                    __typename
                }
            }
        `
    );

    const deleteTagFromAllNotifications = useCallback(
        ({
            activityFeedTagIds,
            itemEventTagIds,
            conversationIds,
            userId,
            hasTradeUserId,
            tag,
        }: DeleteTagFromAllNotificationsInput): void => {
            deleteTagFromAllNotificationsMutation({
                variables: {
                    input: {
                        activityFeedTagIds,
                        itemEventTagIds,
                        conversationIds,
                        userId,
                        tag,
                    },
                    hasTradeUserId,
                    priceBookName: getPriceBookName(document.cookie) || 'DEFAULT',
                    userCountryCode: getUserSessionCountryCode(),
                    userId,
                },
            });
        },
        [deleteTagFromAllNotificationsMutation]
    );

    const markNotificationsAsRead = useCallback(
        ({ activityFeedTagIds, itemEventTagIds }: MarkNotificationsAsReadInput): void => {
            markUserNotificationsAsReadMutation({
                variables: { input: { activityFeedTagIds, itemEventTagIds } },
            });
        },
        [markUserNotificationsAsReadMutation]
    );

    return {
        markNotificationsAsRead,
        deleteTagFromAllNotifications,
        deleteTagFromAllNotificationsInFlight,
    };
};
